import { UnreachableFunction } from 'ui/base/types';

import { createRequestString, getResponseBytes, http } from 'services/base/http';

import { Optional } from 'services/base/types';

import {
  createInvoiceObjects,
  createPlanFirewallObject,
  IAccountsDomain,
  Invoice,
  Pangolin,
  Payment,
  PaymentType,
  PlanFirewallBilling,
  PlanFirewallPackage,
} from './types';

import {
  InvoicePreviewResponse,
} from './protos/invoice_pb';

import {
  DeleteSubscriptionFirewallRequest,
  DeleteSubscriptionFirewallResponse,
  SetPlanFirewallBackendStripe,
  SetPlanFirewallRequest,
  SetPlanFirewallRequestBackendType,
  SetPlanFirewallResponse,
} from './protos/plan_pb';

import { InvoicesService } from './invoices_service';
import { runInAction } from 'mobx';

export class PlanFirewallService {
  static async update(domain: IAccountsDomain,
                      pangolin: Pangolin,
                      payment: Optional<Payment>,
                      package_type: PlanFirewallPackage,
                      billing_type: PlanFirewallBilling,
                      password: string): Promise<{ success: true } |
                                                 { success: false,
                                                   status?: number }> {
    // Determine location of appropriate endpoint
    const endpoint = `/plans/pangolin/${pangolin.id}/firewall`;

    // Construct the request payload
    const requestProto = new SetPlanFirewallRequest();
    requestProto.setPassword(password);
    requestProto.setPackageId(package_type);
    requestProto.setBillingId(billing_type);
    if (payment) {
      requestProto.setPaymentId(payment.id);
      switch (payment.type) {
        case PaymentType.STRIPE:
          requestProto.setBackendType(SetPlanFirewallRequestBackendType.STRIPE);
          requestProto.setStripeData(new SetPlanFirewallBackendStripe());
          break;
        default:
          return UnreachableFunction(payment.type);
      }
    }

    // Issue the request to the server
    const rawPayload = createRequestString(requestProto);
    return await http.post(endpoint, rawPayload).then(async(httpResponse) => {
      // Parse the response from the server
      const responseProto = SetPlanFirewallResponse.deserializeBinary(getResponseBytes(httpResponse));

      // Retrieve the new PlanFirewall object from the response
      const newPlanFirewallDTO = responseProto.getPlan()!;
      const newPlanFirewall = createPlanFirewallObject(newPlanFirewallDTO);

      // Update the Pangolin with the new PlanFirewall object
      runInAction(() => {
        const newPangolin = domain.pangolins.get(pangolin.id);
        if (newPangolin) {
          newPangolin.plans.firewall = newPlanFirewall;
          domain.pangolins.set(pangolin.id, newPangolin);
        }
      });

      // Retrieve new invoices from the server
      await InvoicesService.fetchAll(domain);

      // Return true on success
      return { success: true };
    }).catch((httpError) => {
      // Return false on failure
      return { success: false, status: httpError.response?.status };
    });
  }

  static async preview(domain: IAccountsDomain,
                       pangolin: Pangolin,
                       payment: Optional<Payment>,
                       package_type: PlanFirewallPackage,
                       billing_type: PlanFirewallBilling):
                       Promise<{ success: true,
                                 is_trialing: boolean,
                                 trial_until: Optional<Date>,
                                 charges: Invoice[],
                                 refunds: Invoice[],
                                 notes: string[] } | {
                                 success: false,
                                 status?: number,
                               }> {
    // Determine location of appropriate endpoint
    const endpoint = `/plans/pangolin/${pangolin.id}/firewall/invoice-preview`;

    // Construct the request payload
    const requestProto = new SetPlanFirewallRequest();
    requestProto.setPackageId(package_type);
    requestProto.setBillingId(billing_type);
    if (payment) {
      requestProto.setPaymentId(payment.id);
      switch (payment.type) {
        case PaymentType.STRIPE:
          requestProto.setBackendType(SetPlanFirewallRequestBackendType.STRIPE);
          requestProto.setStripeData(new SetPlanFirewallBackendStripe());
          break;
        default:
          return UnreachableFunction(payment.type);
      }
    }

    // Issue the request to the server
    const rawPayload = createRequestString(requestProto);
    return await http.post(endpoint, rawPayload).then((httpResponse) => {
      const responseProto = InvoicePreviewResponse.deserializeBinary(getResponseBytes(httpResponse));
      return {
        success: true,
        is_trialing: responseProto.getIsTrialing() ?? false,
        trial_until: responseProto.getTrialUntil()?.toDate(),
        charges: createInvoiceObjects(responseProto.getChargesList()),
        refunds: createInvoiceObjects(responseProto.getRefundsList()),
        notes: responseProto.getNotesList(),
      };
    }).catch((httpError) => {
      return {
        success: false,
        status: httpError.response?.status,
      };
    });
  }
}

export class SubscriptionFirewallService {
  static async delete(domain: IAccountsDomain,
                      pangolin: Pangolin,
                      password: string): Promise<{ success: true } |
                                                 { success: false,
                                                   status?: number }> {
    // Determine location of appropriate endpoint
    const endpoint = `/plans/pangolin/${pangolin.id}/firewall/subscription`;

    // Construct the request payload
    const requestProto = new DeleteSubscriptionFirewallRequest();
    requestProto.setPassword(password);

    // Issue the request to the server
    const rawPayload = createRequestString(requestProto);
    return await http.delete(endpoint, { data: rawPayload }).then((httpResponse) => {
      // Parse the response from the server
      const responseProto = DeleteSubscriptionFirewallResponse.deserializeBinary(getResponseBytes(httpResponse));

      // Retrieve the new PlanFirewall object from the response
      const newPlanFirewallDTO = responseProto.getPlan()!;
      const newPlanFirewall = createPlanFirewallObject(newPlanFirewallDTO);

      // Update the Pangolin with the new PlanFirewall object
      runInAction(() => {
        const newPangolin = domain.pangolins.get(pangolin.id);
        if (newPangolin) {
          newPangolin.plans.firewall = newPlanFirewall;
          domain.pangolins.set(pangolin.id, newPangolin);
        }
      });

      // Return true on success
      return { success: true };
    }).catch((httpError) => {
      // Return false on failure
      return { success: false, status: httpError.response?.status };
    });
  }
}
